import { useState, useCallback, useRef, useEffect } from "react"
import { Message, Follow, User } from "../ZeefTypes"
import ZeefService from "../ZeefService"
import { ZeefEventObserver } from "../ZeefWebSocket"

interface ZeefWebSocketEventHandlers {
  onMessageReceived?: (message: Message) => void
  onFollowAdded?: (follow: Follow) => void
  onFollowRemoved?: (follow: Follow) => void
  onUserOnline?: (data: { user_id: string; timestamp: string }) => void
  onUserOffline?: (data: { user_id: string; timestamp: string }) => void
  onError?: (error: string) => void
  onConnectionOpen?: () => void
  onConnectionClose?: (code: number, reason: string) => void
  onConnectionError?: (error: Event) => void
}

interface UseZeefWebSocketReturn {
  // State
  isConnected: boolean

  // Actions
  initializeWebSocket: () => Promise<void>
  cleanup: () => void
  setEventHandlers: (handlers: ZeefWebSocketEventHandlers) => void
}

class ZeefChatEventObserver extends ZeefEventObserver {
  private eventHandlers: ZeefWebSocketEventHandlers = {}

  setEventHandlers(handlers: ZeefWebSocketEventHandlers) {
    this.eventHandlers = handlers
  }

  onEvent(event: any) {
    // General event handler - could be used for logging
  }

  onMessageReceived(message: Message) {
    this.eventHandlers.onMessageReceived?.(message)
  }

  onFollowAdded(follow: Follow) {
    this.eventHandlers.onFollowAdded?.(follow)
  }

  onFollowRemoved(follow: Follow) {
    this.eventHandlers.onFollowRemoved?.(follow)
  }

  onUserOnline(data: { user_id: string; timestamp: string }) {
    this.eventHandlers.onUserOnline?.(data)
  }

  onUserOffline(data: { user_id: string; timestamp: string }) {
    this.eventHandlers.onUserOffline?.(data)
  }

  onConnectionOpen() {
    this.eventHandlers.onConnectionOpen?.()
  }

  onConnectionClose(code: number, reason: string) {
    this.eventHandlers.onConnectionClose?.(code, reason)
  }

  onConnectionError(error: Event) {
    this.eventHandlers.onConnectionError?.(error)
  }

  onError(error: string) {
    this.eventHandlers.onError?.(error)
  }
}

export const useZeefWebSocket = (): UseZeefWebSocketReturn => {
  const [isConnected, setIsConnected] = useState(false)
  const observerRef = useRef<ZeefChatEventObserver | null>(null)

  const initializeWebSocket = useCallback(async () => {
    try {
      // Create observer if it doesn't exist
      if (!observerRef.current) {
        observerRef.current = new ZeefChatEventObserver()
        
        // Set default connection state handlers
        observerRef.current.setEventHandlers({
          onConnectionOpen: () => setIsConnected(true),
          onConnectionClose: () => setIsConnected(false),
          onConnectionError: () => setIsConnected(false),
        })
      }

      await ZeefService.connectWebSocket()
      ZeefService.addWebSocketObserver(observerRef.current)
      setIsConnected(true)
    } catch (error) {
      console.error("Failed to connect to ZEEF WebSocket:", error)
      setIsConnected(false)
    }
  }, [])

  const cleanup = useCallback(() => {
    if (observerRef.current) {
      ZeefService.removeWebSocketObserver(observerRef.current)
    }
    ZeefService.disconnectWebSocket()
    setIsConnected(false)
  }, [])

  const setEventHandlers = useCallback((handlers: ZeefWebSocketEventHandlers) => {
    if (observerRef.current) {
      // Merge with existing connection state handlers
      const mergedHandlers = {
        ...handlers,
        onConnectionOpen: () => {
          setIsConnected(true)
          handlers.onConnectionOpen?.()
        },
        onConnectionClose: (code: number, reason: string) => {
          setIsConnected(false)
          handlers.onConnectionClose?.(code, reason)
        },
        onConnectionError: (error: Event) => {
          setIsConnected(false)
          handlers.onConnectionError?.(error)
        },
      }
      
      observerRef.current.setEventHandlers(mergedHandlers)
    }
  }, [])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup()
    }
  }, [cleanup])

  return {
    // State
    isConnected,

    // Actions
    initializeWebSocket,
    cleanup,
    setEventHandlers,
  }
}
